// @ts-nocheck

import 'leaflet/dist/leaflet.css'

import { useEffect, useRef, useState } from 'react'

import { Box, Icon, Modal, Switch, Text } from '@fairfashion/design-system'
import icon from 'leaflet/dist/images/marker-icon.png'
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  WMSTileLayer,
  useMap,
} from 'react-leaflet'

import { MapProps } from '../MapCard/map-card.types'
import { MapLegend } from '../MapLegend/map-legend.comp'

import * as Styles from './leaflet-map.styles'
import { getMarkerIcon } from './leaftlet-map.utils'

const DefaultIcon = L.icon({
  iconUrl: icon,
})

L.Marker.prototype.options.icon = DefaultIcon

/**
 * This is a workaround to force leaflet to update the map size
 * when the page is loaded
 */
const MapResetSize = ({
  setZoom,
  setBounds,
}: {
  setZoom: (value: number) => void
  setBounds: (value: any) => void
}) => {
  const map = useMap()

  useEffect(() => {
    const logMapInfo = () => {
      const bounds = map.getBounds()
      const zoom = map.getZoom()

      setZoom?.(zoom)
      setBounds?.(bounds)
    }

    logMapInfo()

    map.on('moveend', logMapInfo)
    map.on('zoomend', logMapInfo)

    map.invalidateSize()

    return () => {
      map.off('moveend', logMapInfo)
      map.off('zoomend', logMapInfo)
    }
  }, [map])

  return null
}

export const LeafletMapCustom = ({
  center,
  locations,
  polygons,
  zoom,
  expand,
  setExpand,
  setZoom,
  setBounds,
}: MapProps & { expand: boolean; setExpand: (value: boolean) => void }) => {
  const [showDeforestation, setShowDeforestation] = useState(true)
  const [showLegend, setShowLegend] = useState(false)

  const mapRef = useRef<any>(null)

  if (!center) {
    center = [-16, -55.31]
  }

  return (
    <Styles.MapWrapper position="relative">
      <MapContainer
        center={center}
        zoom={zoom || 4}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <MapResetSize setZoom={setZoom} setBounds={setBounds} />

        <TileLayer
          url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png"
          subdomains="abcd"
          attribution='&copy; <a href="https://carto.com/">Carto</a>'
        />

        <WMSTileLayer
          url="https://geoserver.fairfashion.io/geoserver/ows"
          layers="deforestation:deforestation"
          format="image/png8"
          transparent={true}
          opacity={showDeforestation ? 1 : 0}
          tileSize={256}
        />

        {locations
          ?.filter((location) => location.lat && location.lng)
          .map((location, index) => {
            const customIcon = getMarkerIcon(
              location.color || '#FF0000',
              location.type,
              location.count,
            )

            return (
              <Marker
                key={`marker-${index}`}
                position={[location.lat, location.lng]}
                icon={customIcon}
              >
                <Popup>
                  <a href={location.url || undefined}>
                    <Box direction="column" margin="0" gap={1}>
                      <Text fontWeight="bold" size="small">
                        {location.title}
                      </Text>
                      <Text size="small">{location.description}</Text>
                    </Box>
                  </a>
                </Popup>
              </Marker>
            )
          })}

        {polygons?.map((polygon, index) => (
          <Polygon
            key={`polygon-${index}`}
            pathOptions={{
              color: '#FF0000',
              fillColor: '#FF0000',
              fillOpacity: 0.35,
            }}
            positions={polygon.coordinates[0][0].map((coordinate: any) => [
              coordinate[1],
              coordinate[0],
            ])}
          />
        ))}
      </MapContainer>
      <Styles.MapSwitch>
        <Switch
          onChange={() => setShowDeforestation(!showDeforestation)}
          checked={showDeforestation}
        />
        <Text fontWeight={500} size="small">
          {`${showDeforestation ? 'Mostrar' : 'Ocultar'} desmatamento`}
        </Text>
      </Styles.MapSwitch>

      <Styles.MapButtons>
        <Styles.MapButton onClick={() => setShowLegend(true)}>
          <Text fontWeight={500} size="small">
            Legenda
          </Text>
        </Styles.MapButton>
        <Styles.MapButton onClick={() => setExpand(!expand)}>
          {expand ? (
            <Icon name="fullscreen_exit" />
          ) : (
            <Icon name="fullscreen" />
          )}
        </Styles.MapButton>
      </Styles.MapButtons>

      <MapLegend showLegend={showLegend} setShowLegend={setShowLegend} />
    </Styles.MapWrapper>
  )
}

export const LeafletMap = (props: MapProps) => {
  const [open, setOpen] = useState(false)
  return !open ? (
    <LeafletMapCustom {...props} setExpand={setOpen} expand={open} />
  ) : (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={'Mapa'}
      width="100%"
      height="100%"
      minHeight="100%"
      minWidth="100%"
    >
      <LeafletMapCustom {...props} setExpand={setOpen} expand={open} />
    </Modal>
  )
}
